/* eslint-disable @typescript-eslint/dot-notation */
import { MenuItem } from '@affinis/smartus-components';
import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { Tab } from '@affinis/smartus-components/lib/tabs/tabs.components.types';
import {
  Component,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TokenClaims } from '@azure/msal-common';
import {
  ExtendRoleRequest,
  OrganisationClient,
  OrganisationViewModel,
  RoleClient,
  RoleValidityRequest,
  RoleViewModel,
  UserAdministrationClient,
  UserAdministrationIncidentRequest,
  UserViewModel,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { ContactSearchFormlyFieldService } from '@core/services/contact-search-formly-field-service/contact-search-formly-field.service';
import { EmployeeSearchFormlyFieldService } from '@core/services/employee-search-formly-field-service/employee-search-formly-field.service';
import { ExtendRoleDialogComponent } from '@mandants/telekom/features/administration/extend-role-dialog/extend-role-dialog.component';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { ModalFilterComponent } from '@shared/components/modal-filter/modal-filter.component';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import {
  ITableConfig,
  ITableFilterConfig,
} from '@shared/components/table-base/table-base.types';
import { RegexpEmail } from '@shared/utils/regex';
import {
  isRoleExpired,
  isRoleExtendable,
} from '@shared/utils/role-validity.utils';
import moment from 'moment';
import { BehaviorSubject, forkJoin, map, of, shareReplay } from 'rxjs';

export interface IDialogData {
  columns: MtxGridColumn[];
  filterFields: FormlyFieldConfig[];
  filterValues: ITableFilterConfig;
  actions: {
    onSubmit: (formModel) => void;
    onReset: () => void;
  };
}

/** Eine Zeile innerhalb der Rollen-/Gueltigkeitsspalten einer Benutzerzeile. */
export interface IUserRoleRow {
  id: string;
  description: string;
  validFrom: string;
  validUntil: string;
  /** Tagesbeginn der Gueltigkeit als Zeitstempel, undefined wenn unbegrenzt. Nur zum Filtern. */
  validFromTimestamp: number | undefined;
  /** Tagesbeginn des Gueltigkeitsendes als Zeitstempel, undefined wenn unbegrenzt. Nur zum Filtern. */
  validUntilTimestamp: number | undefined;
  /** Laeuft die Vergabe demnaechst aus und kann daher verlaengert werden? */
  isExtendable: boolean;
  /** Ist die Vergabe bereits abgelaufen? Solche Rollen lassen sich nur noch verlaengern. */
  isExpired: boolean;
}

/** Benutzerzeile des Grids inklusive der aufgeteilten Rollenzeilen. */
export interface IUserRow extends UserViewModel {
  roleRows: IUserRoleRow[];
}

@Component({
  selector: 'app-administration',
  templateUrl: './administration.component.html',
  styleUrls: ['./administration.component.scss'],
  standalone: false,
})
export class AdministrationComponent
  extends TableBaseComponent
  implements OnInit
{
  headline = this.translateService.instant(
    'menu.admin.component.user_administration'
  );

  loadingData = false;

  loading = false;

  override config = this.configure(<ITableConfig>{
    title: 'administration.users.title',
    translatePath: 'administration',
  });

  tabs: Tab[] = [
    {
      label: this.translateService.instant('administration.users.title'),
      id: 'user',
      onClick: (id: string) => this.onTabClick(id),
    },
    {
      label: this.translateService.instant('administration.create_user.title'),
      id: 'create-user',
      onClick: (id: string) => this.onTabClick(id),
    },
  ];

  activeTab = this.tabs[0].id;

  columnsUsers: MtxGridColumn[] = [];

  roles$ = this.roleClient.get().pipe(shareReplay(1));

  private userDataSubject = new BehaviorSubject<IUserRow[]>([]);
  userData = this.userDataSubject.asObservable();

  @ViewChild('roleNamesTemplate', { static: true })
  roleNamesTemplateRef: TemplateRef<void>;

  @ViewChild('roleValidFromTemplate', { static: true })
  roleValidFromTemplateRef: TemplateRef<void>;

  @ViewChild('roleValidUntilTemplate', { static: true })
  roleValidUntilTemplateRef: TemplateRef<void>;

  @ViewChild('roleExtendTemplate', { static: true })
  roleExtendTemplateRef: TemplateRef<void>;

  @ViewChild('roleEndTemplate', { static: true })
  roleEndTemplateRef: TemplateRef<void>;

  chips: Chip[] = [];

  controlButtons: ButtonProps[] = [
    {
      label: this.translateService.instant('crud.refresh'),
      onClick: () => {
        this.refreshData();
      },
    },
  ];

  menuItems: MenuItem[] = [];

  dialogData: IDialogData;

  userDataInitial = [];

  modalComponent = ModalFilterComponent;

  cardUser: CardConfig = {
    header: {
      title: {
        label: this.translate('create_user.headline'),
      },
    },
    footer: {
      button1: {
        label: this.translate('create_user.label__submit'),
        onClick: () => this.validateForm(),
        disabled: false,
      },
      button2: {
        label: this.translate('create_user.label__reset'),
        onClick: () => this.resetCreateUser(),
        disabled: false,
      },
    },
  };

  form = new FormGroup({});

  formModel = {};

  formFields: Array<FormlyFieldConfig>;

  filledForm: Array<FormlyFieldConfig> | null;

  currentUserTokenClaims: TokenClaims & {
    [key: string]: unknown;
  };

  isUserSelected = false;

  private organisationsSubject = new BehaviorSubject<OrganisationViewModel[]>(
    []
  );
  organisations$ = this.organisationsSubject.asObservable();

  isTelekomChecked = false;

  isTelekomUser = false;

  selectedRolesValidFrom: Date | undefined;

  selectedRolesValidUntil: Date | undefined;

  constructor(
    injector: Injector,
    private breadcrumbService: BreadcrumbService,
    private roleClient: RoleClient,
    private employeeFormlyFieldService: EmployeeSearchFormlyFieldService,
    private contactFormlyFieldService: ContactSearchFormlyFieldService,
    private userAdministrationClient: UserAdministrationClient,
    private organisationClient: OrganisationClient,
    private dialog: MatDialog
  ) {
    super(injector);
    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);

    this.userData.subscribe({
      next: res => {
        this.userDataInitial = res;
        this.filterUsers(this.config.grid.filter.filterOptions);
      },
    });
  }

  ngOnInit() {
    this.loadingData = true;

    this.loadUserData();

    this.organisationClient.get().subscribe({
      next: (organisations: OrganisationViewModel[]) => {
        this.organisationsSubject.next(organisations);
      },
    });

    this.currentUserTokenClaims = this.userService.getCurrentUserTokenClaims();

    this.isTelekomUser =
      this.currentUserTokenClaims.ciamId &&
      this.currentUserTokenClaims.ciamId !== '';

    if (this.isTelekomUser) {
      this.isTelekomChecked = true;
    }

    this.columnsUsers = [
      {
        header: this.translateService.instant('administration.users.firstname'),
        field: 'firstName',
        sortable: true,
        width: '13%',
      },
      {
        header: this.translateService.instant('administration.users.lastname'),
        field: 'lastName',
        sortable: true,
        width: '13%',
      },
      {
        header: this.translateService.instant('administration.users.email'),
        field: 'email',
        sortable: true,
        width: '16%',
      },
      {
        header: this.translateService.instant('administration.users.role'),
        field: 'roles',
        cellTemplate: this.roleNamesTemplateRef,
        width: '19%',
      },
      {
        header: this.translateService.instant('administration.users.validFrom'),
        field: 'roleValidFrom',
        cellTemplate: this.roleValidFromTemplateRef,
        width: '9%',
      },
      {
        header: this.translateService.instant(
          'administration.users.validUntil'
        ),
        field: 'roleValidUntil',
        cellTemplate: this.roleValidUntilTemplateRef,
        width: '9%',
      },
      {
        header: this.translateService.instant('administration.users.extend'),
        field: 'roleExtend',
        cellTemplate: this.roleExtendTemplateRef,
        width: '7%',
      },
      {
        header: this.translateService.instant('administration.users.end'),
        field: 'roleEnd',
        cellTemplate: this.roleEndTemplateRef,
        width: '7%',
      },
      {
        header: this.translateService.instant(
          'administration.users.lastSignIn'
        ),
        field: 'lastSignIn',
        sortable: true,
        type: 'date',
        width: '7%',
      },
    ];

    this.filterFields = [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'firstName',
            type: 'input',
            props: {
              label: this.translate('users.firstname'),
            },
          },
          {
            key: 'lastName',
            type: 'input',
            props: {
              label: this.translate('users.lastname'),
            },
          },
          {
            key: 'email',
            type: 'input',
            props: {
              label: this.translate('users.email'),
            },
          },
          {
            key: 'roles',
            type: 'autocomplete-chips-for-roles',
            props: {
              label: this.translate('users.role'),
              options: this.roles$,
            },
          },
          {
            type: 'flex-layout-col',
            fieldGroup: [
              {
                type: 'datepicker',
                key: 'lastSignInDateFrom',
                props: {
                  label: this.translate('users.lastSignInDateFrom'),
                },
              },
              {
                type: 'datepicker',
                key: 'lastSignInDateTo',
                props: {
                  label: this.translate('users.lastSignInDateTo'),
                  maxDate: new Date(),
                },
              },
            ],
          },
          {
            type: 'flex-layout-col',
            fieldGroup: [
              {
                type: 'datepicker',
                key: 'validityDateFrom',
                props: {
                  label: this.translate('users.validityDateFrom'),
                },
              },
              {
                type: 'datepicker',
                key: 'validityDateTo',
                props: {
                  label: this.translate('users.validityDateTo'),
                },
              },
            ],
          },
        ],
      },
    ];

    this.dialogData = {
      columns: this.columns,
      filterFields: this.filterFields,
      filterValues: this.config.grid.filter.filterOptions,
      actions: {
        onSubmit: formModel => {
          this.loadingData = true;
          this.mapFormModelToChips(formModel);
          this.updateFilter(formModel);
          this.filterUsers(this.config.grid.filter.filterOptions);
          this.dialogData.filterValues = this.config.grid.filter.filterOptions;
        },
        onReset: () => {
          this.loadingData = true;
          this.mapFormModelToChips({});
          this.updateFilter({});
          this.filterUsers(this.config.grid.filter.filterOptions);
        },
      },
    };

    if (this.isTelekomChecked) {
      this.formFields = this.getFormFieldsForTelekom();
    } else {
      this.formFields = this.getFormFieldsForExternal();
    }
  }

  refreshData() {
    this.loadingData = true;
    this.loadUserData();
  }

  filterUsers(searchCriteria: Partial<UserViewModel>) {
    this.userData = of(this.userDataInitial);
    const filteringSearchCriteria = searchCriteria.roles?.map(item => item.id);

    // Der Gueltigkeitszeitraum gehoert nicht zum UserViewModel, er stammt nur aus dem Filterdialog.
    const filterCriteria = searchCriteria as Record<string, unknown>;
    const validityFrom = this.toValidityTimestamp(
      filterCriteria['validityDateFrom'] as Date
    );
    const validityUntil = this.toValidityTimestamp(
      filterCriteria['validityDateTo'] as Date
    );
    const hasValidityFilter =
      validityFrom !== undefined || validityUntil !== undefined;

    this.userData = this.userData.pipe(
      map(users =>
        users.filter(user => {
          // Filter by input fields (firstName, lastName, email)
          const inputFiltersPassed = Object.keys(searchCriteria).every(key => {
            const value = searchCriteria[key as keyof UserViewModel];
            if (key === 'roles') return true; // Skip role here, we'll handle it separately

            // Die beiden Grenzen des Gueltigkeitszeitraums muessen gemeinsam gegen
            // dieselbe Rolle geprueft werden, das passiert unterhalb der Schleife.
            if (key === 'validityDateFrom' || key === 'validityDateTo')
              return true;
            if (Array.isArray(user[key as keyof UserViewModel])) {
              this.loadingData = false;
              return (user[key as keyof UserViewModel] as string[]).some(item =>
                item.includes(value as string)
              );
            }

            if (key === 'lastSignInDateFrom') {
              this.loadingData = false;
              if (user.lastSignIn === null) return false;
              const signInDate = new Date(user.lastSignIn).setHours(0, 0, 0, 0);
              const fromDate = new Date(value as Date).setHours(0, 0, 0, 0);
              return signInDate >= fromDate;
            }

            if (key === 'lastSignInDateTo') {
              this.loadingData = false;
              if (user.lastSignIn === null) return false;
              const signInDate = new Date(user.lastSignIn).setHours(0, 0, 0, 0);
              const toDate = new Date(value as Date).setHours(0, 0, 0, 0);
              return signInDate <= toDate;
            }

            this.loadingData = false;
            return user[key as keyof UserViewModel]
              ?.toString()
              .toLowerCase()
              .includes(value?.toString().toLowerCase() || '');
          });
          // Filter by role criteria - abgelaufene Rollen zaehlen ebenfalls,
          // sie stehen wie die aktuellen in den Rollenzeilen.
          const roleFilterPassed =
            !filteringSearchCriteria ||
            filteringSearchCriteria?.length === 0 ||
            filteringSearchCriteria.every(criteria =>
              user.roleRows.some(roleRow => roleRow.id === criteria)
            );
          const validityFilterPassed =
            !hasValidityFilter ||
            this.matchingRoleRows(user.roleRows, validityFrom, validityUntil)
              .length > 0;
          this.loadingData = false;
          return inputFiltersPassed && roleFilterPassed && validityFilterPassed;
        })
      ),
      // Bei gesetztem Zeitraum bleiben nur die Rollen stehen, die ihn treffen.
      map(users =>
        hasValidityFilter
          ? users.map(
              user =>
                <IUserRow>{
                  ...user,
                  roleRows: this.matchingRoleRows(
                    user.roleRows,
                    validityFrom,
                    validityUntil
                  ),
                }
            )
          : users
      )
    );
  }

  override getTableData$ = () => {
    return this.userData;
  };

  onTabClick(value: string) {
    this.activeTab = value;
  }

  handleOnRemoveChip = eventObj => {
    // Find the index of the item to remove in this.config.grid.filter.filterOptions.roles
    const indexToRemove =
      this.config.grid.filter.filterOptions.roles?.findIndex(
        item => item.id === eventObj.id
      );
    // Remove the item from the roles array if found
    if (indexToRemove !== -1) {
      this.config.grid.filter.filterOptions.roles?.splice(indexToRemove, 1);
    }
    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);

    delete this.config.grid.filter.filterOptions[eventObj.id];

    this.filterUsers(this.config.grid.filter.filterOptions);
  };

  mapFormModelToChips = formModel => {
    this.chips = [];
    this.chips.length = 0;
    Object.keys(formModel).forEach(formModelItemKey => {
      const flat = this.filterFields[0].fieldGroup.flatMap(
        x => x.fieldGroup ?? x
      );
      const field = flat.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });
      if (formModelItemKey === 'roles') {
        formModel[formModelItemKey].forEach(item => {
          this.chips.push({
            id: `${item.id}`,
            label: `${field.props.label}: ${item.description}`,
          });
        });
      } else if (
        formModelItemKey === 'lastSignInDateFrom' ||
        formModelItemKey === 'lastSignInDateTo' ||
        formModelItemKey === 'validityDateFrom' ||
        formModelItemKey === 'validityDateTo'
      ) {
        const date = new Date(formModel[formModelItemKey]).toLocaleDateString(
          'de-DE'
        );
        this.chips.push({
          id: formModelItemKey,
          label: `${field.props.label}: ${date}`,
        });
      } else if (formModel[formModelItemKey]) {
        this.chips.push({
          id: formModelItemKey,
          label: `${field.props.label}: ${formModel[formModelItemKey]}`,
        });
      }
    });
  };

  onSelectUser(_id: string, _initialSearch, user) {
    this.formFields[0].form
      ?.get('userData.firstName')
      ?.setValue(user.firstName);
    this.formFields[0].form?.get('userData.lastName')?.setValue(user.lastName);
    this.formFields[0].form?.get('userData.email')?.setValue(user.email);
    this.formFields[0].form
      ?.get('userData.organisation')
      ?.setValue(user.company);

    this.formFields[0].form?.get('userData.firstName').disable();
    this.formFields[0].form?.get('userData.lastName').disable();
    this.formFields[0].form?.get('userData.email').disable();

    this.isUserSelected = true;
  }

  onDelete() {
    this.formFields[0].form?.get('userData.firstName')?.setValue(null);
    this.formFields[0].form?.get('userData.lastName')?.setValue(null);
    this.formFields[0].form?.get('userData.email')?.setValue(null);
    this.formFields[0].form?.get('userData.organisation')?.setValue(null);

    if (!this.isTelekomChecked) {
      this.formFields[0].form?.get('userData.firstName').enable();
      this.formFields[0].form?.get('userData.lastName').enable();
      this.formFields[0].form?.get('userData.email').enable();
      this.formFields[0].form?.get('userData.roles').enable();
    }

    this.isUserSelected = false;
  }

  resetCreateUser() {
    this.formModel = {};
    this.form.markAsUntouched();
    this.selectedRolesValidFrom = undefined;
    this.selectedRolesValidUntil = undefined;
    this.formFields[0].form?.get('userData.roles').markAsUntouched();
  }

  validateForm() {
    if (this.form.valid) {
      this.createUser();
    } else {
      this.form.markAllAsTouched();
      this.formFields[0].form?.get('userData.roles').markAsTouched();
    }
  }

  onRolesSelectionChange(result: { validFrom: Date; validUntil: Date }) {
    this.selectedRolesValidFrom = result.validFrom;
    this.selectedRolesValidUntil = result.validUntil;
  }

  private toDateOnlyString(
    value: Date | string | undefined
  ): string | undefined {
    return value ? moment(value).format('YYYY-MM-DD') : undefined;
  }

  /**
   * Gueltigkeitszeitraum je Rolle. Jede Rolle traegt den Zeitraum, der beim
   * Hinzufuegen im Dialog gewaehlt wurde.
   */
  private toRoleValidities(roles: RoleViewModel[]): RoleValidityRequest[] {
    return (roles ?? []).map(role => ({
      roleId: role.id,
      validFrom: this.toDateOnlyString(role.validFrom) as unknown as Date,
      validUntil: this.toDateOnlyString(role.validUntil) as unknown as Date,
    }));
  }

  createUser() {
    this.loading = true;
    let request: UserAdministrationIncidentRequest;

    if (this.isUserSelected) {
      request = {
        firstName:
          this.form.value['contact']?.searchResult?.firstName ??
          this.form.value['employee']?.searchResult?.firstName ??
          null,
        lastName:
          this.form.value['contact']?.searchResult?.lastName ??
          this.form.value['employee']?.searchResult?.lastName ??
          null,
        email:
          this.form.value['contact']?.searchResult?.email ??
          this.form.value['employee']?.searchResult?.email ??
          null,
        companyId:
          this.form.value['contact']?.searchResult?.company?.id ?? null,
        b2cUserId: this.form.value['employee']?.searchResult?.id ?? null,
        contactId: this.form.value['contact']?.searchResult?.contactId ?? null,
        roles:
          this.form.value['userData']?.roles?.map(
            (role: RoleViewModel) => role.id
          ) ?? null,
        roleValidities: this.toRoleValidities(
          this.form.value['userData']?.roles
        ),
        validFrom: this.toDateOnlyString(
          this.selectedRolesValidFrom
        ) as unknown as Date,
        validUntil: this.toDateOnlyString(
          this.selectedRolesValidUntil
        ) as unknown as Date,
      };
    } else {
      request = {
        firstName: this.form.value['userData']?.firstName ?? null,
        lastName: this.form.value['userData']?.lastName ?? null,
        email: this.form.value['userData']?.email ?? null,
        companyId: this.form.value['userData']?.organisation ?? null,
        b2cUserId: null,
        contactId: null,
        roles:
          this.form.value['userData']?.roles?.map(
            (role: RoleViewModel) => role.id
          ) ?? null,
        roleValidities: this.toRoleValidities(
          this.form.value['userData']?.roles
        ),
        validFrom: this.toDateOnlyString(
          this.selectedRolesValidFrom
        ) as unknown as Date,
        validUntil: this.toDateOnlyString(
          this.selectedRolesValidUntil
        ) as unknown as Date,
      };
    }

    this.userAdministrationClient.createUser(request).subscribe({
      next: () => {
        this.toastr.success(
          this.translateService.instant(
            'administration.create_user.success_message'
          )
        );
        this.loading = false;
        this.form.reset();
        this.isUserSelected = false;
        this.isTelekomChecked = false;
        this.selectedRolesValidFrom = undefined;
        this.selectedRolesValidUntil = undefined;
        this.formFields = this.getFormFieldsForExternal();
      },
      error: err => {
        let message = this.translateService.instant('generic.errorMessage');
        if (err.status === 409) {
          message = this.translateService.instant(
            'administration.create_user.error_message_user_exists'
          );
        }
        this.toastr.error(
          message,
          this.translateService.instant('alert.error')
        );
        this.loading = false;
      },
    });
  }

  getFormFieldsForExternal() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'isTelekomChecked',
            type: 'select',
            defaultValue: this.isTelekomChecked,
            props: {
              options: [
                {
                  id: 1,
                  label: this.translateService.instant(
                    'administration.create_user.telekom_employee'
                  ),
                  value: true,
                },
                {
                  id: 2,
                  label: this.translateService.instant(
                    'administration.create_user.external_user'
                  ),
                  value: false,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field.formControl.valueChanges.subscribe(value => {
                  this.isTelekomChecked = value;

                  field.formControl.disable({ emitEvent: false });
                  this.toggleTelekomCheck();
                  field.formControl.enable({ emitEvent: false });
                });
              },
            },
          },
          {
            wrappers: [],
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translate(
                    'create_user.label__searchContact'
                  ),
                },
              },
              {
                key: 'contact',
                fieldGroup: [
                  ...this.contactFormlyFieldService.autocompleteSearch(
                    this.translate('create_user.placeholder__search'),
                    this.onSelectUser.bind(this),
                    false,
                    true,
                    null,
                    true,
                    this.onDelete.bind(this)
                  ),
                ],
              },
            ],
          },
          {
            type: 'flex-layout',
            key: 'userData',
            fieldGroup: [
              {
                key: 'firstName',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__firstName'),
                  required: true,
                },
              },
              {
                key: 'lastName',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__lastName'),
                  required: true,
                },
              },
              {
                key: 'email',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__email'),
                  required: true,
                  pattern: RegexpEmail,
                },
                validation: {
                  messages: {
                    pattern: (_err, _field: FormlyFieldConfig) =>
                      this.translateService.instant('formErrors.email.pattern'),
                  },
                },
              },
              {
                key: 'organisation',
                type: 'select',
                props: {
                  label: this.translate('create_user.label__organisation'),
                  required: true,
                  options: this.organisations$,
                  valueProp: 'id',
                  labelProp: 'name',
                },
              },
              {
                // Externe bekommen die Auswahl per Modal inklusive
                // Gueltigkeitszeitraum je Rolle, wie in der Benutzeranlage.
                key: 'roles',
                type: 'button-select-roles',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__selectRoles'
                  ),
                  rolesLabel: this.translate('create_user.label__roles'),
                  dialogTitle: 'user.request.create.dialog.title',
                  required: true,
                  options: this.roles$.pipe(
                    map(roles =>
                      roles
                        .filter(role => role.name.startsWith('DL_'))
                        .sort((a, b) =>
                          a.description.localeCompare(b.description)
                        )
                    )
                  ),
                  onSelectionChange: result =>
                    this.onRolesSelectionChange(result),
                },
                validation: {
                  messages: {
                    required: this.translateService.instant(
                      'formErrors.required',
                      {
                        label: this.translate('create_user.label__roles'),
                      }
                    ),
                  },
                },
              },
            ],
          },
        ],
      },
    ];
  }

  getFormFieldsForTelekom() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'isTelekomChecked',
            type: 'select',
            defaultValue: this.isTelekomChecked,
            props: {
              options: [
                {
                  id: 1,
                  label: this.translateService.instant(
                    'administration.create_user.telekom_employee'
                  ),
                  value: true,
                },
                {
                  id: 2,
                  label: this.translateService.instant(
                    'administration.create_user.external_user'
                  ),
                  value: false,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field.formControl.valueChanges.subscribe(value => {
                  this.isTelekomChecked = value;

                  field.formControl.disable({ emitEvent: false });
                  this.toggleTelekomCheck();
                  field.formControl.enable({ emitEvent: false });
                });
              },
            },
          },
          {
            wrappers: [],
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translate(
                    'create_user.label__searchPerson'
                  ),
                },
              },
              {
                key: 'employee',
                fieldGroup: [
                  ...this.employeeFormlyFieldService.autocompleteSearch(
                    this.translate('create_user.placeholder__search'),
                    this.onSelectUser.bind(this),
                    false,
                    true,
                    null,
                    true,
                    this.onDelete.bind(this)
                  ),
                ],
              },
            ],
          },
          {
            type: 'flex-layout',
            key: 'userData',
            fieldGroup: [
              {
                key: 'firstName',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__firstName'),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'lastName',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__lastName'),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'email',
                type: 'input',
                props: {
                  label: this.translate('create_user.label__email'),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'roles',
                type: 'autocomplete-chips-for-roles',
                props: {
                  label: this.translate('create_user.label__roles'),
                  required: true,
                  options: this.roles$.pipe(
                    map(roles =>
                      roles
                        .filter(
                          role =>
                            role.name.startsWith('REM_') ||
                            role.name.startsWith('DTAG_') ||
                            role.name.startsWith('IT_')
                        )
                        .sort((a, b) =>
                          a.description.localeCompare(b.description)
                        )
                    )
                  ),
                },
                validation: {
                  messages: {
                    required: this.translateService.instant(
                      'formErrors.required',
                      {
                        label: this.translate('create_user.label__roles'),
                      }
                    ),
                  },
                },
              },
            ],
          },
        ],
      },
    ];
  }

  toggleTelekomCheck() {
    this.isUserSelected = false;

    // Die Rollenauswahl wird neu aufgebaut, ein zuvor gewaehlter Zeitraum
    // gehoert nicht mehr dazu - Telekom-Mitarbeitende haben ohnehin keinen.
    this.selectedRolesValidFrom = undefined;
    this.selectedRolesValidUntil = undefined;

    if (this.isTelekomChecked) {
      this.formFields = this.getFormFieldsForTelekom();
    } else {
      this.formFields = this.getFormFieldsForExternal();
    }

    this.form.patchValue(
      {
        isTelekomChecked: this.isTelekomChecked,
      },
      { emitEvent: false }
    );
  }

  navigateToDetails(userData: UserViewModel) {
    userData.roles = userData.roles.filter(role => role.description);
    this.roles$.subscribe({
      next: roles => {
        this.router.navigateByUrl('user-administration/details', {
          state: {
            userId: userData.id,
            roles: userData.isTelekomEmployee
              ? roles
                  .filter(
                    role =>
                      role.name.startsWith('REM_') ||
                      role.name.startsWith('DTAG_') ||
                      role.name.startsWith('IT_')
                  )
                  .sort((a, b) => a.description.localeCompare(b.description))
              : roles
                  .filter(role => role.name.startsWith('DL_'))
                  .sort((a, b) => a.description.localeCompare(b.description)),
            organisations: this.organisationsSubject.getValue(),
          },
        });
      },
    });
  }

  loadUserData() {
    forkJoin({
      users: this.userClient.getAll(),
      roles: this.roles$,
    }).subscribe({
      next: ({ users, roles }) => {
        this.userDataSubject.next(
          users.map(user => this.toUserRow(user, roles))
        );
        this.loadingData = false;
      },
      error: () => {
        this.toastr.error(
          this.translateService.instant('administration.users.error_load_data'),
          this.translateService.instant('alert.error')
        );
        this.loadingData = false;
      },
    });
  }

  /**
   * Baut je Benutzer eine Zeile mit einer Unterzeile pro zugewiesener Rolle auf.
   * Bereits abgelaufene Rollen stehen mit dabei, damit sie verlaengert werden
   * koennen. Rollen, die nicht (mehr) in der Rollenliste enthalten sind, werden
   * ausgeblendet.
   */
  private toUserRow(user: UserViewModel, roles: RoleViewModel[]): IUserRow {
    // Vorzeitig entzogene Rollen enden am Tag des Entzugs. Sie gelten daher
    // ueber ihre Herkunft als abgelaufen, nicht ueber das Datum.
    const expiredRoleIds = new Set(
      (user.expiredRoles ?? []).map(role => role.id)
    );

    const roleRows = [...(user.roles ?? []), ...(user.expiredRoles ?? [])]
      .map(userRole => {
        const role = roles.find(item => item.id === userRole.id);
        if (!role) {
          return null;
        }
        return <IUserRoleRow>{
          id: userRole.id,
          description: role.description,
          validFrom: this.formatValidityDate(userRole.validFrom),
          validUntil: this.formatValidityDate(userRole.validUntil),
          validFromTimestamp: this.toValidityTimestamp(userRole.validFrom),
          validUntilTimestamp: this.toValidityTimestamp(userRole.validUntil),
          isExtendable: isRoleExtendable(userRole.validUntil),
          isExpired:
            expiredRoleIds.has(userRole.id) ||
            isRoleExpired(userRole.validUntil),
        };
      })
      .filter(roleRow => roleRow !== null)
      .sort((a, b) => a.description.localeCompare(b.description));

    return { ...user, roleRows };
  }

  /**
   * Der generierte Client typisiert die Gueltigkeiten als Date, konvertiert sie aber
   * nicht, sodass zur Laufzeit der rohe Datums-String aus dem JSON ankommt.
   */
  private formatValidityDate(value: Date | string | undefined): string {
    if (!value) {
      return '--';
    }
    return moment(value)
      .locale(this.translateService.instant('locale.locale'))
      .format(
        this.translateService.instant('locale.moment__date_format_only_digits')
      );
  }

  /**
   * Oeffnet den Dialog zum Verlaengern einer Rollenvergabe und stellt bei
   * Bestaetigung ein neues Ticket ein.
   */
  openExtendRoleDialog(user: IUserRow, roleRow: IUserRoleRow) {
    const dialogRef = this.dialog.open(ExtendRoleDialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '600px',
      data: {
        userName: this.userDisplayName(user),
        roleDescription: roleRow.description,
        currentValidUntil: roleRow.validUntil,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (validUntil: Date | undefined) => {
        if (validUntil) {
          this.extendRole(user, roleRow, validUntil);
        }
      },
    });
  }

  /**
   * Fragt nach und beendet die Rollenvergabe anschliessend.
   */
  openEndRoleDialog(user: IUserRow, roleRow: IUserRoleRow) {
    const dialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'administration.users.end_dialog.title'
        ),
        message: this.translateService.instant(
          'administration.users.end_dialog.message',
          { role: roleRow.description, user: this.userDisplayName(user) }
        ),
        okButtonText: this.translateService.instant(
          'administration.users.end_dialog.confirm'
        ),
        dismissButtonText: this.translateService.instant('shared.cancel'),
        isContactForm: false,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.endRole(user, roleRow);
        }
      },
    });
  }

  /**
   * Verlaengert die Vergabe, indem dieselbe Rolle ab heute bis zum neuen Ende
   * erneut beantragt wird.
   */
  private extendRole(user: IUserRow, roleRow: IUserRoleRow, validUntil: Date) {
    this.loading = true;

    const request: ExtendRoleRequest = {
      b2cUserId: user.id,
      roleId: roleRow.id,
      validUntil: this.toDateOnlyString(validUntil) as unknown as Date,
    };

    this.userAdministrationClient.extendRole(request).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success(
          this.translateService.instant(
            'administration.users.extend_dialog.success'
          )
        );
        this.refreshData();
      },
      error: () => {
        this.loading = false;
        this.toastr.error(
          this.translateService.instant(
            'administration.users.extend_dialog.error'
          )
        );
      },
    });
  }

  /**
   * Beendet die Vergabe, indem der Benutzer ohne diese Rolle gespeichert wird.
   * Den Entzug leitet das Backend aus der fehlenden Rolle ab.
   */
  private endRole(user: IUserRow, roleRow: IUserRoleRow) {
    this.loading = true;

    const remainingRoles = (user.roles ?? []).filter(
      role => role.id !== roleRow.id
    );

    const request: UserAdministrationIncidentRequest = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      companyId: null,
      b2cUserId: user.id,
      contactId: user.contactId,
      roles: remainingRoles.map(role => role.id),
      roleValidities: this.toRoleValidities(remainingRoles),
    };

    this.userAdministrationClient.updateUser(request).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success(
          this.translateService.instant(
            'administration.users.end_dialog.success'
          )
        );
        this.refreshData();
      },
      error: () => {
        this.loading = false;
        this.toastr.error(
          this.translateService.instant('administration.users.end_dialog.error')
        );
      },
    });
  }

  /** Name der Person fuer die Dialoge, mit der E-Mail als Rueckfallebene. */
  private userDisplayName(user: IUserRow): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name || (user.email ?? '');
  }

  /**
   * Reduziert eine Gueltigkeitsgrenze auf den Tagesbeginn, damit Filter und Rolle
   * unabhaengig von der Uhrzeit vergleichbar sind. Eine fehlende Grenze bleibt offen.
   */
  private toValidityTimestamp(
    value: Date | string | undefined
  ): number | undefined {
    if (!value) {
      return undefined;
    }
    return moment(value).startOf('day').valueOf();
  }

  /**
   * Liefert die Rollen der Zeile, die der Filter trifft.
   *
   * "Gueltigkeit von" grenzt nach unten ab: Rollen, die vor diesem Tag bereits
   * abgelaufen sind, fallen heraus. "Gueltigkeit bis" begrenzt dagegen das Ende
   * der Rolle selbst - angezeigt wird nur, was bis zu diesem Tag gueltig ist,
   * nicht was darueber hinaus laeuft.
   *
   * Nur Rollen mit hinterlegtem Gueltig von und Gueltig bis kommen in Frage:
   * eine Rolle ohne Zeitraum ist keine befristete Zuweisung, laeuft also
   * unbegrenzt und faellt damit auch aus "bis" heraus. Eine fehlende Grenze im
   * Filter selbst bleibt offen. Ohne Filter bleibt die Liste unveraendert.
   */
  private matchingRoleRows(
    roleRows: IUserRoleRow[],
    filterFrom: number | undefined,
    filterUntil: number | undefined
  ): IUserRoleRow[] {
    if (filterFrom === undefined && filterUntil === undefined) {
      return roleRows ?? [];
    }

    return (roleRows ?? []).filter(roleRow => {
      if (
        roleRow.validFromTimestamp === undefined ||
        roleRow.validUntilTimestamp === undefined
      ) {
        return false;
      }

      return (
        (filterFrom === undefined ||
          roleRow.validUntilTimestamp >= filterFrom) &&
        (filterUntil === undefined ||
          roleRow.validUntilTimestamp <= filterUntil)
      );
    });
  }
}
